import { useNavigate } from 'react-router-dom'

import { Box, Divider } from '@factorialco/deprecated-design-system'
import { F0Button, F0Dialog } from '@factorialco/f0-react'
import { Input } from '@factorialco/f0-react/dist/experimental'
import { Delete, Plus } from '@factorialco/f0-react/icons/app'

import { observer } from 'mobx-react'

import { useForm } from 'modules/core/hooks/useForm'
import { Result, UnhandledResult, handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

const getNameError = (errors: Record<string, unknown>, index: number): string | undefined => {
  const value = errors[`names_${index}`]
  return typeof value === 'string' ? value : undefined
}

export interface CreateSubprojectsDialogProps {
  onSubmit: (values: CreateSubprojectsFormValues) => Promise<UnhandledResult<Result<unknown>>>
  isOpen: boolean
  loading?: boolean
}

export interface CreateSubprojectsFormValues {
  names: string[]
}

const CreateSubprojectsDialog = ({ onSubmit, isOpen, loading }: CreateSubprojectsDialogProps) => {
  const navigate = useNavigate()
  const onClose = () => {
    navigate(-1)
  }

  const form = useForm<CreateSubprojectsFormValues>({
    defaultValues: {
      names: [''],
    },
    validate: (data) => {
      const errors: Record<string, string> = {}

      data.names.forEach((name, index) => {
        const isLastPhase = index === data.names.length - 1
        const shouldSkipValidation = isLastPhase && !name.trim() && data.names.length > 1

        if (!shouldSkipValidation && !name.trim()) {
          errors[`names_${index}`] = i18n.t('project_management.modals.error_messages.mandatory')
        }
      })

      return errors
    },
    onSubmit: async (data) => {
      const filteredNames = data.names.filter((name) => name.trim() !== '')
      if (filteredNames.length === 0) {
        return { success: false, errors: [] }
      }

      const result = await onSubmit({
        names: filteredNames,
      })

      const handled = handleResult(result, (errors) => {
        if (errors.names)
          errors.names = i18n.t('project_management.modals.error_messages.subproject_name_exists')

        form.setErrors(errors)
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        onClose()
      }

      return handled
    },
  })

  const handleSubmit = async (names: string[]) => {
    const filteredNames = names.filter((name) => name.trim() !== '')
    if (filteredNames.length === 0) {
      return
    }

    const result = await onSubmit({
      names: filteredNames,
    })

    const handled = handleResult(result, (errors) => {
      if (errors.names)
        errors.names = i18n.t('project_management.modals.error_messages.subproject_name_exists')

      form.setErrors(errors)
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })

      onClose()
    }

    return handled
  }

  const addPhase = () => {
    form.setValues({ names: [...form.values.names, ''] })
  }

  const removePhase = (index: number) => {
    const newNames = form.values.names.filter((_, i) => i !== index)
    form.setValues({ names: newNames.length > 0 ? newNames : [''] })
  }

  const updatePhase = (index: number, value: string) => {
    const newNames = [...form.values.names]
    newNames[index] = value
    form.setValues({ names: newNames })
  }

  return (
    <F0Dialog isOpen={isOpen} onClose={onClose} title="Create phase">
      <Box flexDirection="column" gap="s16">
        {form.values.names.map((name, index) => (
          <Box key={index} flexDirection="row" gap="s8" alignItems="flexEnd" width="full">
            <Box flexGrow style={{ minWidth: '0' }}>
              <Input
                size="md"
                label=""
                placeholder={i18n.t('project_management.export.subproject_name')}
                value={name}
                onChange={(value) => updatePhase(index, value)}
                error={getNameError(form.errors, index)}
              />
            </Box>
            <Box alignSelf="flexStart">
              <F0Button
                hideLabel
                icon={Delete}
                onClick={() => removePhase(index)}
                variant="ghost"
              />
            </Box>
          </Box>
        ))}
        <Box alignItems="flexStart">
          <F0Button
            label={i18n.t('project_management.subprojects.add_phase')}
            onClick={addPhase}
            variant="outline"
            size="md"
            icon={Plus}
          />
        </Box>
        <Divider />
        <Box alignItems="flexEnd">
          <F0Button
            label={i18n.t('common.create')}
            onClick={() => {
              handleSubmit(form.values.names)
            }}
            size="md"
            loading={loading}
          />
        </Box>
      </Box>
    </F0Dialog>
  )
}

export default observer(CreateSubprojectsDialog)
